import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {Button} from 'UI/components/action/Button';

import {useGroupListening} from 'UI/hooks/useGroupListening';

/**
 * Bestimmt die Beschriftung des Teilen-/Kopieren-Buttons: eine Bestätigung nach erfolgreichem
 * Kopieren, sonst je nach Geräteunterstützung "Teilen" oder "Link kopieren".
 *
 * @param copied   Ob der Link gerade eben kopiert wurde.
 * @param canShare Ob das native Share-Sheet auf diesem Gerät verfügbar ist.
 * @param t        Die Übersetzungsfunktion.
 * @returns Die Button-Beschriftung.
 */
const resolveShareButtonLabel = (copied: boolean, canShare: boolean, t: (key: string) => string): string => {
    if (copied) return t('group.copiedMessage');

    return canShare ? t('group.shareButton') : t('group.copyLinkButton');
};

/**
 * Settings-Sektion für Gruppen-Hören: erstellt eine Hörgruppe, zeigt QR-Code und Fallback-Code,
 * die Liste verbundener Mitglieder mit Entfernen-Buttons, und einen "Gruppe beenden"-Button.
 *
 * @returns ReactNode der Gruppen-Hören-Sektion.
 */
const GroupListeningSection = () => {
    const {
        canShare,
        copied,
        createGroup,
        endGroup,
        group,
        isCreating,
        kickMember,
        members,
        qrDataUrl,
        rotateCode,
        shareLink,
        t
    } = useGroupListening();

    if (!group) {
        return (
            <Wrapper data-cy="GroupListeningSection">
                <Button
                    data-cy="GroupListeningSection-create"
                    disabled={isCreating}
                    size="small"
                    type="button"
                    variant="primaryGradient"
                    onClick={() => {
                        void createGroup();
                    }}
                >
                    {isCreating ? t('group.creating') : t('group.createButton')}
                </Button>
            </Wrapper>
        );
    }

    return (
        <Wrapper data-cy="GroupListeningSection">
            {qrDataUrl && <QrImage alt={t('group.title')} data-cy="GroupListeningSection-qr" src={qrDataUrl} />}
            <CodeLabel>{t('group.joinLinkLabel')}</CodeLabel>
            <LinkRow>
                <JoinLink data-cy="GroupListeningSection-link" href={group.joinUrl} rel="noreferrer" target="_blank">
                    {group.joinUrl}
                </JoinLink>
                <Button
                    data-cy="GroupListeningSection-share"
                    size="small"
                    type="button"
                    variant="ghost"
                    onClick={() => {
                        void shareLink();
                    }}
                >
                    {resolveShareButtonLabel(copied, canShare, t)}
                </Button>
            </LinkRow>
            <CodeLabel>{t('group.joinCodeLabel')}</CodeLabel>
            <JoinCode data-cy="GroupListeningSection-code">{group.joinCode}</JoinCode>
            <MemberCount data-cy="GroupListeningSection-memberCount">
                {t('group.membersConnected', {count: members.length})}
            </MemberCount>
            {members.length > 0 && (
                <MemberList data-cy="GroupListeningSection-members">
                    {members.map(member => (
                        <MemberRow key={member.id}>
                            <MemberStatusDot $connected={member.connected} />
                            <MemberName>{member.displayName}</MemberName>
                            <RemoveButton
                                aria-label={t('group.removeMember')}
                                data-cy={`GroupListeningSection-kick-${member.id}`}
                                type="button"
                                onClick={() => {
                                    void kickMember(member.id);
                                }}
                            >
                                {t('group.removeMember')}
                            </RemoveButton>
                        </MemberRow>
                    ))}
                </MemberList>
            )}
            <ButtonRow>
                <Button
                    data-cy="GroupListeningSection-rotate"
                    size="small"
                    type="button"
                    variant="ghost"
                    onClick={() => {
                        void rotateCode();
                    }}
                >
                    {t('group.rotateCodeButton')}
                </Button>
                <Button
                    data-cy="GroupListeningSection-end"
                    size="small"
                    type="button"
                    variant="ghost"
                    onClick={() => {
                        void endGroup();
                    }}
                >
                    {t('group.endButton')}
                </Button>
            </ButtonRow>
        </Wrapper>
    );
};

GroupListeningSection.displayName = 'GroupListeningSection';

export {GroupListeningSection};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
    /* Ohne diese beiden Zeilen schrumpft der Wrapper wegen align-items:flex-start auf die
       Inhaltsbreite seines breitesten Kindes (der ungekürzten Join-URL in LinkRow), statt die
       tatsächlich verfügbare Breite der AccountCard zu übernehmen. */
    min-width: 0;
    width: 100%;
`;

const QrImage = styled.img`
    aspect-ratio: 1;
    border-radius: 8px;
    height: 18rem;
    max-width: 100%;
    width: 18rem;
`;

const CodeLabel = styled.span`
    color: ${({theme}) => theme.colors.inkTertiary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.3rem;
`;

const LinkRow = styled.div`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(3)};
    max-width: 100%;
    width: 100%;
`;

const JoinLink = styled.a`
    color: ${({theme}) => theme.colors.primaryDefault};
    flex: 1 1 auto;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    /* Ohne min-width:0 verweigert sich dieses Flex-Item dem Schrumpfen unter seine
       Inhaltsbreite, wodurch text-overflow:ellipsis bei einer langen URL nie greift. */
    min-width: 0;
    overflow: hidden;
    text-decoration: underline;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const JoinCode = styled.span`
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 2.4rem;
    font-weight: 700;
    letter-spacing: 0.3rem;
`;

const MemberCount = styled.span`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
`;

const MemberList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    width: 100%;
`;

const MemberRow = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(2)};
`;

/** Props for `MemberStatusDot`. */
interface MemberStatusDotProps {
    /** Whether this member is currently connected. */
    $connected: boolean;
}

const MemberStatusDot = styled.span<MemberStatusDotProps>`
    background: ${({$connected, theme}) => ($connected ? theme.colors.primaryDefault : theme.colors.inkDisabled)};
    border-radius: 50%;
    flex-shrink: 0;
    height: 0.8rem;
    width: 0.8rem;
`;

const MemberName = styled.span`
    flex: 1;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: ${({theme}) => theme.colors.accentDefault};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
`;

const ButtonRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(3)};
`;